/* eslint-disable no-use-before-define */
import {
  ResolvedClientSessionData,
  ResolvedPartnerSessionData,
} from '../hooks/queries/useSessionPlQuery';
import { getAnalyticsReportingMode, usesAnalyticsStatsUi } from '../utils/analyticsReportingMode';
import {
  canCreateAutomatedReport,
  getAutomatedReportingNavStateForSession,
} from '../utils/automatedReportingNav';

type Perm = {
  sessionPlData?: ResolvedClientSessionData | ResolvedPartnerSessionData;
};

type PermFunction = (ctx: Perm) => boolean;

export const perms = {
  accountParameters: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'client' && !!sessionPlData?.permissions.self_serve,
  bookingsManager: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'client' &&
    sessionPlData?.permissions.allowBookingsManager !== 'none' &&
    !!sessionPlData.permissions.self_serve,
  accountRules: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'client' && !!sessionPlData?.permissions.self_serve,
  accountSettings: ({ sessionPlData }) => !!sessionPlData?.permissions.dianomi,
  accountUsers: ({ sessionPlData }) => !!sessionPlData,
  adForm: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'client' && !!sessionPlData?.permissions.self_serve,
  adManager: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'client' && !!sessionPlData?.permissions.self_serve,
  analytics: ({ sessionPlData }) => !!sessionPlData?.permissions.campaign,
  automatedReporting: ({ sessionPlData }) => {
    if (!sessionPlData?.permissions?.campaign) {
      return false;
    }
    return !getAutomatedReportingNavStateForSession(sessionPlData).hidden;
  },
  automatedReportingCreate: ({ sessionPlData }) => canCreateAutomatedReport(sessionPlData),
  analyticsStats: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'client' &&
    usesAnalyticsStatsUi(getAnalyticsReportingMode(sessionPlData)),
  billing: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'client' && !!sessionPlData?.nav.billing,
  conversionTrackers: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'client' && !!sessionPlData.permissions.self_serve,
  insights: ({ sessionPlData }) => !!sessionPlData?.nav.insights,
  invoiceRoute: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'client' && !!sessionPlData?.nav.billing,
  isDianomi: ({ sessionPlData }) => !!sessionPlData?.login.is_dianomi,
  leads: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'client' && !!sessionPlData?.nav.myLeads,
  newClient: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'client' && !!sessionPlData?.permissions.allow_create_client,
  overview: ({ sessionPlData }) => !!sessionPlData?.permissions.campaign,
  publisherAds: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'partner' && !!sessionPlData?.permissions.ads,
  publisherPaymentHistory: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'partner' && !!sessionPlData?.permissions.campaign,
  loggedIn: ({ sessionPlData }) => !!sessionPlData,
  audienceManager: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'client' &&
    ((!!sessionPlData.permissions.showAudienceManager && !!sessionPlData.permissions.self_serve) ||
      (!!sessionPlData.permissions.cohorts?.dianomi && !!sessionPlData.permissions.self_serve)),
  supplyManager: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'client' &&
    !!sessionPlData?.permissions.self_serve &&
    sessionPlData.permissions.inventoryAccessLevel !== 'none',
  supplyManagerRouteAccess: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'client' &&
    !!sessionPlData?.permissions.self_serve &&
    ['advanced', 'basic', 'supply_only'].includes(sessionPlData.permissions.inventoryAccessLevel),
  /** Sidebar item and `publisher-bidding` route loader (advanced / basic / campaign_only). */
  publisherBiddingRouteAccess: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'client' &&
    !!sessionPlData?.permissions.self_serve &&
    ['advanced', 'basic', 'campaign_only'].includes(sessionPlData.permissions.inventoryAccessLevel),
  contextfeedManager: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'partner' && !!sessionPlData.permissions.allowContextfeedManager,
  deviceBidManagerClient: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'client' &&
    !!sessionPlData?.permissions.device_manager?.bid.match(/client|all/) &&
    !!sessionPlData?.permissions.self_serve,
  deviceBidManagerProduct: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'client' &&
    !!sessionPlData?.permissions.device_manager?.bid.match(/product|all/) &&
    !!sessionPlData?.permissions.self_serve,
  campaignPlanner: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'client' && !!sessionPlData?.permissions.allowCampaignPlanner,
  domainManager: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'client' && !!sessionPlData?.permissions.allowDomainManager,
  polls: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'client' &&
    !!sessionPlData?.permissions.self_serve &&
    !!sessionPlData?.client?.timezone_active,
  /** Manage shared views: create/update/delete account-scoped views. Staff or client admins. */
  manageSharedViews: ({ sessionPlData }) =>
    !!sessionPlData?.login?.is_dianomi ||
    (sessionPlData?.accountType === 'client' && !!sessionPlData?.permissions?.admin),
  /** Composite permissions for inventory access - must combine permissions of any feature that sits under inventory */
  /** isClient and selfserve are mandatory. The account must then meet one of the remaining conditions. */
  inventory: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'client' &&
    !!sessionPlData?.permissions.self_serve &&
    (sessionPlData?.permissions.inventoryAccessLevel !== 'none' ||
      !!sessionPlData?.permissions.device_manager?.bid.match(/client|all/) ||
      !!sessionPlData?.permissions.device_manager?.bid.match(/product|all/) ||
      !!sessionPlData?.permissions.allowDomainManager),
  /** Create/list/revoke analytics API credentials. Staff or client admins on Genie 2.0 clients. */
  apiCredentials: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'client' &&
    usesAnalyticsStatsUi(getAnalyticsReportingMode(sessionPlData)) &&
    (!!sessionPlData?.login?.is_dianomi || !!sessionPlData?.permissions?.admin),
  celtraReports: ({ sessionPlData }) => !!sessionPlData?.permissions.campaign,
  dappierReports: ({ sessionPlData }) => !!sessionPlData?.permissions.campaign,
  isDeveloper: ({ sessionPlData }) => {
    try {
      const devFlag = localStorage.getItem(atob('ZGV2ZWxvcGVyQWNjZXNz')); // TODO: Consider other ways
      return devFlag === 'true' && !!sessionPlData?.login.is_dianomi;
    } catch {
      return false;
    }
  },

  /** Parent admin managing a child's column access while staying on the parent account (Genie 2.0 only). */
  parentChildColumnAccess: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'client' &&
    !!sessionPlData?.permissions?.admin &&
    sessionPlData?.isChild !== true &&
    usesAnalyticsStatsUi(getAnalyticsReportingMode(sessionPlData)),
  /** Parent hub to list/create children and open column access (Genie 2.0 only). */
  childAccountsHub: ({ sessionPlData }) =>
    sessionPlData?.accountType === 'client' &&
    sessionPlData?.isChild !== true &&
    usesAnalyticsStatsUi(getAnalyticsReportingMode(sessionPlData)) &&
    (!!sessionPlData?.permissions?.allow_create_client ||
      (!!sessionPlData?.permissions?.admin && !!sessionPlData?.isParent)),
} as const satisfies Record<string, PermFunction>;
